import React from 'react';
import AkButton from '@atlaskit/button';
import CloseIcon from '@atlaskit/icon/core/migration/cross';
import SearchIcon from '@atlaskit/icon/core/migration/search';
import Search from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { Box, media, xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { fg } from '@atlassian/jira-feature-gating';

type PropsSearchFieldIconDynamic = {
	hasValue: boolean;
	searchIconVisible: boolean;
	clearIconLabel: string;
	// @deprecated remove when cleaning up visual-refresh FG
	primaryColor?: string;
	onClear: () => void;
	onlyIconSpacing?: boolean;
	inCustomSearchField?: boolean;
};

/**
 * Provides a dynamic icon within a search field that toggles between a search and clear icon based on user input, allowing for an interactive and responsive search experience.
 */
export const SearchFieldIconDynamic = ({
	onClear,
	hasValue,
	searchIconVisible,
	clearIconLabel,
	primaryColor,
	onlyIconSpacing = false,
	inCustomSearchField = false,
}: PropsSearchFieldIconDynamic) => {
	const akSearchIcon = (
		<SearchIcon
			LEGACY_size="small"
			label=""
			LEGACY_primaryColor={primaryColor}
			color="currentColor"
		/>
	);
	const searchIcon = searchIconVisible ? akSearchIcon : <></>;

	return (
		<Box
			xcss={[
				wrapperStyles,
				onlyIconSpacing && spacingStyles,
				inCustomSearchField && fg('jira_jif_jam_clear_text_icon') && minimalSpacingStyles,
			]}
		>
			{
				// eslint-disable-next-line no-nested-ternary
				hasValue ? (
					fg('jira_jif_jam_clear_text_icon') ? (
						<Pressable
							xcss={iconWrapperStyles}
							onClick={onClear}
							// Clear button should not be focusable
							// see https://hello.atlassian.net/wiki/spaces/A11YKB/pages/3031993460/Clear+options+on+an+input+field
							tabIndex={-1}
							testId="searchfield.ui.searchfield-icon.clear-button"
							aria-label={clearIconLabel}
						>
							<CrossCircleIcon label="" />
						</Pressable>
					) : (
						<AkButton
							appearance="subtle"
							spacing="none"
							// eslint-disable-next-line jira/integration/enforce-data-testid-usage
							data-test-id="searchfield.ui.searchfield-icon.clear-button"
							testId="searchfield.ui.searchfield-icon.clear-button"
							onClick={onClear}
							iconBefore={
								<CloseIcon
									LEGACY_size="small"
									label={clearIconLabel}
									LEGACY_primaryColor={primaryColor}
									color="currentColor"
								/>
							}
						/>
					)
				) : (
					searchIcon
				)
			}
		</Box>
	);
};

type PropsSearchFieldIcon = {
	searchIconVisible: boolean;
	onlyIconSpacing?: boolean;
};

export const SearchFieldIcon = ({
	searchIconVisible,
	onlyIconSpacing = false,
}: PropsSearchFieldIcon) => (
	<Box xcss={[wrapperStylesBefore, onlyIconSpacing && spacingStylesBefore]}>
		{searchIconVisible ? <Search spacing="none" label="" color={token('color.icon')} /> : <></>}
	</Box>
);

const wrapperStyles = xcss({
	color: 'color.text.accent.gray',
	marginTop: 'space.0',
	// the wider marginRight is applicable for when the search/clear icon is after the input
	marginRight: 'space.100',
	marginBottom: 'space.0',
	marginLeft: 'space.0',
	height: '16px',
	display: 'flex',
	alignItems: 'center',
});

// if this icon is used in a custom search field, we won't assume any spacing since we don't know where their searchIcon is used
// i.e. before or after the input
// it will be up to the consumers to add spacing where needed
const minimalSpacingStyles = xcss({
	marginRight: 'space.0',
});

const spacingStyles = xcss({
	marginRight: 'space.075',
	[media.above.md]: {
		marginRight: 'space.100',
	},
});

const wrapperStylesBefore = xcss({
	display: 'flex',
	marginRight: 'space.0',
	marginLeft: 'space.075',
	[media.above.md]: {
		marginLeft: 'space.100',
	},
});

const spacingStylesBefore = xcss({
	marginLeft: 'space.075',
	[media.above.md]: {
		marginLeft: 'space.100',
	},
});

const iconWrapperStyles = xcss({
	display: 'flex',
	cursor: 'pointer',
	paddingInlineStart: 'space.0',
	paddingInlineEnd: 'space.0',
	paddingBlockStart: 'space.0',
	paddingBlockEnd: 'space.0',
	borderRadius: 'radius.full',
	color: 'color.text.subtlest',
	':hover': {
		color: 'color.text.accent.gray',
	},
});
